/**
 * CustomerDetail - Customer profile, balance, and transaction history
 * 
 * TODO: REFACTORING NEEDED (Code Quality Review)
 * This component has grown to 800+ lines and should be split into:
 * - CustomerInfo: Profile display and editing
 * - BalanceSummary: Account balance, receivable, payable display
 * - TransactionActions: Payment, advance, refund, payout buttons
 * - PurchaseHistory: Recent purchases table
 * - CreditLedger: Financial ledger table with running balance (uses customer_financial_ledger collection)
 * 
 * For now, the code is organized with clear section comments.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, MapPin, User, ShoppingBag, Wallet, Pencil, Save, X, TrendingUp,
  Banknote, PiggyBank, Undo2, Scale, RefreshCw, HandCoins, Store, Trash2,
} from "lucide-react";
import { formatDate, formatDateTime } from "@/lib/format";
import {
  ReceivePaymentDialog,
  RecordAdvanceDialog,
  IssueRefundDialog,
  PayOutDialog,
  EditLedgerEntryDialog,
  MarkCreditDialog,
} from "@/pages/customer";
import { SaleDetailDialog, CancelSaleDialog } from "@/pages/sales";
import SalesReturnDialog from "@/pages/sales/SalesReturnDialog";
import NewSaleDialog from "@/pages/NewSaleDialog";

// =====================================================================
// MAIN COMPONENT
// =====================================================================
export default function CustomerDetail() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEditLedger = user?.role === "master" || user?.role === "super_user";

  const [customer, setCustomer] = useState(null);
  const [stats, setStats] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [balance, setBalance] = useState(null);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  // Dialogs
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);  // NEW: Mark Credit dialog
  const [editLedgerOpen, setEditLedgerOpen] = useState(false);
  const [editLedgerEntry, setEditLedgerEntry] = useState(null);
  const [cleaningLedger, setCleaningLedger] = useState(false);
  const [resettingLedger, setResettingLedger] = useState(false);
  const [viewSale, setViewSale] = useState(null); // Sale detail dialog
  
  // Sale action dialogs (Edit, Return, Cancel)
  const [editSaleTarget, setEditSaleTarget] = useState(null);
  const [returnSaleTarget, setReturnSaleTarget] = useState(null);
  const [cancelSaleTarget, setCancelSaleTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      // Fetch customer info and stats from existing endpoints
      const [custRes, statsRes, salesRes] = await Promise.all([
        api.get(`/customers/${customerId}`),
        api.get(`/customers/${customerId}/stats`),
        api.get("/sales", { params: { customer_id: customerId, limit: 50 } }),
      ]);
      
      // Fetch from NEW financial ledger endpoints for balance and ledger data
      // This is the new robust Debit/Credit system
      let balanceData = null;
      let ledgerData = null;
      
      try {
        const [balRes, ledgerRes] = await Promise.all([
          api.get(`/financial-ledger/balance/${customerId}`),
          api.get(`/financial-ledger/ledger/${customerId}`, { params: { limit: 200 } }),
        ]);
        balanceData = balRes.data;
        ledgerData = ledgerRes.data;
      } catch (flErr) {
        // Fallback to old system if new ledger fails (e.g., not migrated yet)
        console.warn("Financial ledger not available, falling back to old system:", flErr);
        const [balRes, ledgerRes] = await Promise.all([
          api.get(`/credit/${customerId}`),
          api.get(`/customers/${customerId}/ledger`),
        ]);
        balanceData = balRes.data;
        ledgerData = ledgerRes.data;
      }
      
      setCustomer(custRes.data);
      setStats(statsRes.data);
      setLedger(ledgerData);
      setSales(salesRes.data.items || []);
      setBalance(balanceData);
      setForm({
        name: custRes.data.name || "",
        place: custRes.data.place || "",
        phone1: custRes.data.phone1 || "",
        phone2: custRes.data.phone2 || "",
        notes: custRes.data.notes || "",
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Ledger cleanup - rebuilds ledger from actual sales/payment data
  const handleLedgerCleanup = async () => {
    if (user?.role !== "master") {
      toast.error("Only MASTER users can perform ledger cleanup. Please contact your administrator.");
      return;
    }
    
    if (!window.confirm(
      "This will rebuild the credit ledger from actual sales and payment data.\n\n" +
      "• All sale entries will be recreated\n" +
      "• Cancellation reversals will be added\n" +
      "• Payment/advance entries will be preserved\n\n" +
      "Are you sure you want to proceed?"
    )) {
      return;
    }
    
    setCleaningLedger(true);
    try {
      const { data } = await api.post(`/customers/${customerId}/ledger-cleanup`);
      toast.success(
        `Ledger cleanup complete: ${data.old_entries} → ${data.new_entries} entries. ` +
        `Balance: ₹${data.old_balance} → ₹${data.new_balance}`
      );
      load(); // Refresh data
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCleaningLedger(false);
    }
  };

  // Ledger reset - zeros out the balance with a RESET entry
  const handleLedgerReset = async () => {
    if (user?.role !== "master") {
      toast.error("Only MASTER users can perform ledger reset. Please contact your administrator.");
      return;
    }
    
    if (!window.confirm(
      "This will ZERO OUT the current credit ledger balance.\n\n" +
      "• A single 'RESET' entry will be added to zero the balance\n" +
      "• Use this for write-offs or external settlements\n" +
      "• This action cannot be undone\n\n" +
      "Are you sure you want to proceed?"
    )) {
      return;
    }
    
    setResettingLedger(true);
    try {
      const { data } = await api.post(`/customers/${customerId}/ledger-reset`);
      toast.success(
        `Ledger reset complete: Balance ₹${data.previous_balance} → ₹${data.new_balance}`
      );
      load(); // Refresh data
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResettingLedger(false);
    }
  };

  useEffect(() => {
    if (customerId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const saveChanges = async () => {
    setSaving(true);
    try {
      await api.patch(`/customers/${customerId}`, form);
      toast.success("Customer updated");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-muted-foreground">Loading customer...</div>;
  }

  if (!customer) {
    return (
      <div className="p-6 text-center">
        <div className="text-muted-foreground mb-4">Customer not found</div>
        <Button onClick={() => navigate("/customers")} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Customers
        </Button>
      </div>
    );
  }

  // Use the canonical balance from /api/financial-ledger/balance/{id} endpoint (single source of truth)
  // OR fallback to /api/credit/{id} for backwards compatibility
  //
  // IMPORTANT: For "Amount Receivable" display, we use stats.credit_owed which is
  // calculated directly from SALES collection (total - amount_paid - refund_total).
  // This matches the OVERVIEW and MANAGE tables for consistency.
  //
  // The financial ledger is kept as an audit trail but may be out of sync if
  // sales were edited before the customer-change fix was deployed.
  //
  // stats.credit_owed = calculated from unpaid sales (most accurate for display)
  // stats.store_credit = from financial ledger (accurate for advances/overpayments)
  const isNewLedger = balance?.total_debit !== undefined;
  const netBalance = Number(isNewLedger ? balance?.balance : balance?.net_balance) || 0;
  
  // Prioritize stats.credit_owed for receivable (matches OVERVIEW/MANAGE tables)
  const receivableAmt = Number(stats?.credit_owed ?? balance?.receivable ?? 0);
  const payableAmt = Number(stats?.store_credit ?? balance?.payable ?? 0);
  
  const balStatus = receivableAmt > 0.01 ? "customer_owes" : 
                    payableAmt > 0.01 ? "shop_owes" : "settled";

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl mx-auto" data-testid="customer-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/customers")}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-heading font-semibold">{customer.name}</h1>
          {customer.place && (
            <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
              <MapPin className="w-4 h-4" /> {customer.place}
            </div>
          )}
        </div>
        <Button
          variant={editing ? "outline" : "default"}
          size="sm"
          onClick={() => setEditing(!editing)}
          style={!editing ? { backgroundColor: "#9E6D5B" } : {}}
          data-testid="customer-edit-toggle"
        >
          {editing ? <X className="w-4 h-4 mr-1" /> : <Pencil className="w-4 h-4 mr-1" />}
          {editing ? "Cancel" : "Edit"}
        </Button>
      </div>

      {/* Inline Quick-Action Bar */}
      <Card className="p-3 flex flex-wrap items-center gap-2" data-testid="customer-actions-bar">
        <Button
          size="sm"
          onClick={() => setPaymentOpen(true)}
          disabled={receivableAmt <= 0}
          style={{ backgroundColor: "#2D6A4F" }}
          className="text-white disabled:opacity-50"
          data-testid="action-receive-payment"
        >
          <Banknote className="w-3.5 h-3.5 mr-1" />
          Receive Payment
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setAdvanceOpen(true)}
          data-testid="action-record-advance"
        >
          <PiggyBank className="w-3.5 h-3.5 mr-1" />
          Record Advance
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setCreditOpen(true)}
          className="border-amber-300 text-amber-700 hover:bg-amber-50"
          data-testid="action-mark-credit"
        >
          <TrendingUp className="w-3.5 h-3.5 mr-1" />
          Mark Credit
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setRefundOpen(true)}
          data-testid="action-issue-refund"
        >
          <Undo2 className="w-3.5 h-3.5 mr-1" />
          Issue Refund
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setPayoutOpen(true)}
          disabled={payableAmt <= 0}
          className="disabled:opacity-50"
          data-testid="action-payout"
          title={payableAmt <= 0 ? "No store credit balance to settle" : "Pay out / settle the customer's store credit"}
        >
          <HandCoins className="w-3.5 h-3.5 mr-1" />
          Pay Out / Settle
        </Button>
        <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          <Button variant="ghost" size="sm" onClick={load} title="Refresh balance" data-testid="balance-refresh-detail">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </Card>

      {/* Unified Balance Summary - Single Net Balance */}
      <Card className="p-4 sm:p-5" data-testid="customer-balance-summary">
        <div className="flex items-center gap-2 mb-3">
          <Scale className="w-4 h-4 text-[#9E6D5B]" />
          <h2 className="text-sm uppercase tracking-widest text-muted-foreground">Account Balance</h2>
          <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold ${
            balStatus === "customer_owes" ? "bg-orange-100 text-orange-700" :
            balStatus === "shop_owes" ? "bg-emerald-100 text-emerald-700" :
            "bg-muted text-muted-foreground"
          }`}>
            {balStatus === "customer_owes" ? "Customer Owes" :
              balStatus === "shop_owes" ? "Shop Owes" : "Settled"}
          </span>
        </div>
        
        {/* Single Unified Net Balance Card */}
        <div className={`p-5 rounded-lg border-2 ${
          balStatus === "customer_owes" ? "bg-orange-50 border-orange-200" :
          balStatus === "shop_owes" ? "bg-emerald-50 border-emerald-200" :
          "bg-slate-50 border-slate-200"
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-xs uppercase tracking-widest font-semibold ${
                balStatus === "customer_owes" ? "text-orange-600" :
                balStatus === "shop_owes" ? "text-emerald-600" :
                "text-slate-500"
              }`}>
                {balStatus === "customer_owes" ? "Amount Receivable" :
                  balStatus === "shop_owes" ? "Amount Payable" : "Balance"}
              </div>
              <div className={`text-4xl font-mono-data font-bold mt-2 ${
                balStatus === "customer_owes" ? "text-orange-700" :
                balStatus === "shop_owes" ? "text-emerald-700" :
                "text-slate-600"
              }`} data-testid="bal-net">
                ₹{(balStatus === "customer_owes" ? receivableAmt : balStatus === "shop_owes" ? payableAmt : 0).toFixed(2)}
              </div>
              <div className={`text-sm mt-2 ${
                balStatus === "customer_owes" ? "text-orange-600" :
                balStatus === "shop_owes" ? "text-emerald-600" :
                "text-slate-500"
              }`}>
                {balStatus === "customer_owes" ? "Customer needs to pay this amount" :
                  balStatus === "shop_owes" ? "Shop needs to pay/credit this amount" : "No outstanding balance"}
              </div>
            </div>
            <div className={`text-6xl opacity-20 ${
              balStatus === "customer_owes" ? "text-orange-500" :
              balStatus === "shop_owes" ? "text-emerald-500" :
              "text-slate-400"
            }`}>
              {balStatus === "customer_owes" ? "↓" : balStatus === "shop_owes" ? "↑" : "="}
            </div>
          </div>
        </div>

        {/* Breakdown for transparency (collapsible/secondary info) */}
        {(receivableAmt > 0 || payableAmt > 0) && (
          <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-3 text-sm">
            <div className="p-3 rounded bg-muted/50">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Credit Sales Outstanding</div>
              <div className="text-lg font-mono-data font-semibold text-orange-600 mt-1" data-testid="bal-receivable">
                ₹{receivableAmt.toFixed(2)}
              </div>
              <div className="text-[10px] text-muted-foreground">Unpaid invoices</div>
            </div>
            <div className="p-3 rounded bg-muted/50">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Advances / Store Credit</div>
              <div className="text-lg font-mono-data font-semibold text-emerald-600 mt-1" data-testid="bal-payable">
                ₹{payableAmt.toFixed(2)}
              </div>
              <div className="text-[10px] text-muted-foreground">Customer&apos;s credit balance</div>
            </div>
          </div>
        )}

        {/* Pending sales breakdown */}
        {balance?.credit_sales_pending?.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              Pending Credit Sales ({balance.credit_sales_pending.length})
            </div>
            <div className="space-y-1.5 max-h-44 overflow-y-auto">
              {balance.credit_sales_pending.map((s) => (
                <div
                  key={s.sale_id}
                  className="flex items-center gap-3 text-xs p-2 rounded bg-muted/40"
                  data-testid={`pending-sale-${s.sale_id}`}
                >
                  <div className="font-mono-data text-muted-foreground">{formatDate(s.created_at)}</div>
                  <div className="flex-1 truncate">{s.shop_name} <span className="text-muted-foreground">· {s.sale_type}</span></div>
                  <div className="font-mono-data text-muted-foreground">Total ₹{Number(s.total).toFixed(2)}</div>
                  <div className="font-mono-data text-emerald-700">Paid ₹{Number(s.amount_paid).toFixed(2)}</div>
                  <div className="font-mono-data font-semibold text-orange-700">Due ₹{Number(s.balance).toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cancelled-sale refund pending */}
        {balance?.cancelled_refund_items?.length > 0 && (
          <div className="mt-4 pt-4 border-t" data-testid="cancelled-refund-section">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">
                Cancelled Sale Refund Pending ({balance.cancelled_refund_items.length})
              </div>
              <span className="text-[10px] text-muted-foreground">
                · Customer paid for sales that were later cancelled — issue a Pay Out / Refund to settle.
              </span>
            </div>
            <div className="space-y-1.5 max-h-44 overflow-y-auto">
              {balance.cancelled_refund_items.map((s) => (
                <div
                  key={s.sale_id}
                  className="flex items-center gap-3 text-xs p-2 rounded bg-amber-50 border border-amber-100"
                  data-testid={`cancelled-sale-${s.sale_id}`}
                >
                  <div className="font-mono-data text-muted-foreground">{formatDate(s.created_at)}</div>
                  <div className="flex-1 truncate">{s.shop_name} <span className="text-muted-foreground">· cancelled</span></div>
                  <div className="font-mono-data text-muted-foreground">Total ₹{Number(s.total).toFixed(2)}</div>
                  <div className="font-mono-data text-emerald-700">Customer Paid ₹{Number(s.amount_paid_original).toFixed(2)}</div>
                  <div className="font-mono-data font-semibold text-amber-800">Refund ₹{Number(s.pending_refund).toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Total Spent</div>
          <div className="text-2xl font-mono-data font-semibold text-[#9E6D5B] mt-1">
            ₹{(stats?.total_spent || 0).toFixed(2)}
          </div>
          <div className="text-xs text-muted-foreground">{stats?.count || 0} purchases</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Net Balance</div>
          <div className={`text-2xl font-mono-data font-semibold mt-1 ${
            receivableAmt > 0.01 ? "text-orange-600" : payableAmt > 0.01 ? "text-emerald-600" : "text-muted-foreground"
          }`}>
            ₹{(receivableAmt > 0.01 ? receivableAmt : payableAmt > 0.01 ? payableAmt : 0).toFixed(2)}
          </div>
          <div className="text-xs text-muted-foreground">
            {receivableAmt > 0.01 ? "Customer owes" : payableAmt > 0.01 ? "Shop owes" : "Settled"}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Last Purchase</div>
          <div className="text-sm font-medium mt-1">
            {stats?.last_purchase_at ? formatDate(stats.last_purchase_at) : "Never"}
          </div>
          {stats?.last_purchase_amount > 0 && (
            <div className="text-xs text-muted-foreground">
              ₹{stats.last_purchase_amount.toFixed(2)} at {stats.last_shop_name || "—"}
            </div>
          )}
        </Card>
      </div>

      {/* Customer Info */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-5">
          <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
            <User className="w-4 h-4" />
            Customer Information
          </h2>
          {editing ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Place</Label>
                  <Input value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Phone 1</Label>
                  <Input value={form.phone1} onChange={(e) => setForm({ ...form, phone1: e.target.value })} className="mt-1 font-mono-data" />
                </div>
                <div>
                  <Label className="text-xs">Phone 2</Label>
                  <Input value={form.phone2} onChange={(e) => setForm({ ...form, phone2: e.target.value })} className="mt-1 font-mono-data" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" placeholder="Internal notes..." />
              </div>
              <Button onClick={saveChanges} disabled={saving || !form.name.trim()} className="w-full" style={{ backgroundColor: "#9E6D5B" }}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <span>{customer.place || "—"}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span className="font-mono-data">{customer.phone1 || "—"}</span>
                {customer.phone2 && (
                  <span className="font-mono-data text-muted-foreground">/ {customer.phone2}</span>
                )}
              </div>
              {customer.notes && (
                <div className="text-muted-foreground italic mt-2 pt-2 border-t">{customer.notes}</div>
              )}
              <div className="text-xs text-muted-foreground mt-4 pt-2 border-t">
                <div>Created: {formatDateTime(customer.created_at)}</div>
                {customer.updated_at && <div>Updated: {formatDateTime(customer.updated_at)}</div>}
              </div>
            </div>
          )}
        </Card>

        {/* Purchase Breakdown */}
        <Card className="p-5">
          <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Purchase Breakdown
          </h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-md">
              <div>
                <div className="text-sm font-medium text-green-800">Retail Purchases</div>
                <div className="text-xs text-green-600">{stats?.retail_count || 0} transactions</div>
              </div>
              <div className="font-mono-data font-semibold text-green-700">
                ₹{(stats?.retail_total || 0).toFixed(2)}
              </div>
            </div>
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-md">
              <div>
                <div className="text-sm font-medium text-blue-800">Wholesale Purchases</div>
                <div className="text-xs text-blue-600">{stats?.wholesale_count || 0} transactions</div>
              </div>
              <div className="font-mono-data font-semibold text-blue-700">
                ₹{(stats?.wholesale_total || 0).toFixed(2)}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Sales */}
      <Card className="p-5">
        <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
          <ShoppingBag className="w-4 h-4" />
          Recent Purchases ({sales.length})
        </h2>
        {sales.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">No purchase history yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 text-xs uppercase tracking-wider text-muted-foreground">Date</th>
                  <th className="py-2 text-xs uppercase tracking-wider text-muted-foreground">Shop</th>
                  <th className="py-2 text-xs uppercase tracking-wider text-muted-foreground">Type</th>
                  <th className="py-2 text-xs uppercase tracking-wider text-muted-foreground">Items</th>
                  <th className="py-2 text-xs uppercase tracking-wider text-muted-foreground text-right">Total</th>
                  <th className="py-2 text-xs uppercase tracking-wider text-muted-foreground text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale) => (
                  <tr
                    key={sale.id}
                    className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/50 cursor-pointer"
                    onClick={() => setViewSale(sale)}
                  >
                    <td className="py-2">{formatDate(sale.created_at)}</td>
                    <td className="py-2">{sale.shop_name || "—"}</td>
                    <td className="py-2">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        sale.type === "wholesale" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"
                      }`}>
                        {sale.type || "retail"}
                      </span>
                    </td>
                    <td className="py-2">{sale.line_items?.length || 0}</td>
                    <td className="py-2 text-right font-mono-data">₹{Number(sale.total || 0).toFixed(2)}</td>
                    <td className="py-2 text-right">
                      {(() => {
                        // Calculate balance: total - amount_paid - refund_total
                        const total = Number(sale.total || 0);
                        const paid = Number(sale.amount_paid || 0);
                        const refund = Number(sale.refund_total || 0);
                        const balance = total - paid - refund;
                        
                        if (sale.status === "canceled") {
                          return <span className="text-xs text-red-600">Canceled</span>;
                        } else if (balance > 0.01) {
                          // Show balance with paid info if partially paid
                          if (paid > 0) {
                            return (
                              <span className="text-xs text-orange-600 font-mono-data">
                                Bal ₹{balance.toFixed(0)}/{total.toFixed(0)}
                              </span>
                            );
                          }
                          return <span className="text-xs text-orange-600">₹{balance.toFixed(2)} due</span>;
                        } else {
                          return <span className="text-xs text-green-600">Paid</span>;
                        }
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Credit Ledger - Financial Ledger */}
      {ledger?.entries && ledger.entries.length > 0 && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Wallet className="w-4 h-4" />
              Credit Ledger
            </h2>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleLedgerReset}
                disabled={resettingLedger || cleaningLedger}
                className="h-7 text-xs border-orange-200 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                title={user?.role === "master" ? "Zero out ledger balance with a RESET entry" : "Only MASTER users can perform this action"}
                data-testid="ledger-reset-btn"
              >
                <RefreshCw className={`w-3 h-3 mr-1.5 ${resettingLedger ? "animate-spin" : ""}`} />
                {resettingLedger ? "Resetting..." : "Ledger Reset"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLedgerCleanup}
                disabled={cleaningLedger || resettingLedger}
                className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                title={user?.role === "master" ? "Rebuild ledger from actual sales data" : "Only MASTER users can perform this action"}
                data-testid="ledger-cleanup-btn"
              >
                <Trash2 className="w-3 h-3 mr-1.5" />
                {cleaningLedger ? "Cleaning..." : "Ledger Cleanup"}
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 text-xs uppercase tracking-wider text-muted-foreground">Date</th>
                  <th className="py-2 text-xs uppercase tracking-wider text-muted-foreground">Shop</th>
                  <th className="py-2 text-xs uppercase tracking-wider text-muted-foreground">Type</th>
                  <th className="py-2 text-xs uppercase tracking-wider text-muted-foreground">Description</th>
                  <th className="py-2 text-xs uppercase tracking-wider text-muted-foreground text-right">Credit</th>
                  <th className="py-2 text-xs uppercase tracking-wider text-muted-foreground text-right">Debit</th>
                  <th className="py-2 text-xs uppercase tracking-wider text-muted-foreground text-right">Balance</th>
                  {canEditLedger && (
                    <th className="py-2 text-xs uppercase tracking-wider text-muted-foreground text-center w-16">Edit</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {ledger.entries.map((entry, idx) => {
                  const isCredit = entry.credit > 0;
                  const isDebit = entry.debit > 0;
                  const balance = entry.running_balance;
                  const txnType = (entry.transaction_type || "").toLowerCase();
                  
                  return (
                    <tr key={entry.id || `${txnType}-${entry.transaction_datetime}-${idx}`} className="border-b border-[hsl(var(--border))] hover:bg-muted/30">
                      <td className="py-2">{formatDateTime(entry.transaction_datetime)}</td>
                      <td className="py-2">
                        {entry.shop_name || "—"}
                      </td>
                      <td className="py-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          isCredit ? "bg-emerald-50 text-emerald-700" : 
                          isDebit ? "bg-amber-50 text-amber-700" : 
                          "bg-muted text-muted-foreground"
                        }`}>
                          {txnType === "sale" ? "invoice" : 
                           txnType === "sale_cancellation" ? "canceled" :
                           txnType === "store_credit_consumed" ? "payout" :
                           txnType === "sales_return" ? "return" :
                           txnType}
                        </span>
                      </td>
                      <td className="py-2 max-w-[280px] text-xs">
                        <div className="line-clamp-2 whitespace-normal break-words">
                          {entry.notes ? (
                            entry.notes
                          ) : entry.description ? (
                            entry.description
                          ) : entry.reference_id && !entry.reference_id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i) ? (
                            <span className="font-mono">{entry.reference_id}</span>
                          ) : txnType === "advance" ? (
                            "Customer advance"
                          ) : txnType === "payment" ? (
                            `Payment received${entry.payment_mode ? ` (${entry.payment_mode.toUpperCase()})` : ""}`
                          ) : txnType === "store_credit_consumed" ? (
                            "Store credit payout"
                          ) : txnType === "sale" ? (
                            entry.reference_id ? <span className="font-mono">{entry.reference_id}</span> : "Invoice"
                          ) : "—"}
                          {entry.edited_at && (
                            <span className="text-[9px] text-orange-500 ml-1">(edited)</span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 text-right font-mono-data text-emerald-600">
                        {entry.credit > 0 ? `₹${entry.credit.toFixed(2)}` : "—"}
                      </td>
                      <td className="py-2 text-right font-mono-data text-red-600">
                        {entry.debit > 0 ? `₹${entry.debit.toFixed(2)}` : "—"}
                      </td>
                      <td className={`py-2 text-right font-mono-data font-semibold ${
                        balance > 0 ? "text-amber-700" : balance < 0 ? "text-emerald-700" : "text-muted-foreground"
                      }`}>
                        {balance >= 0 ? `₹${balance.toFixed(2)}` : `-₹${Math.abs(balance).toFixed(2)}`}
                      </td>
                      {canEditLedger && (
                        <td className="py-2 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => {
                              setEditLedgerEntry({
                                id: entry.id,
                                type: entry.transaction_type,
                                amount: entry.debit || entry.credit,
                                payment_mode: entry.payment_mode,
                                notes: entry.notes || entry.description,
                                shop_id: entry.shop_id,
                                shop_name: entry.shop_name,
                                created_at: entry.transaction_datetime,
                                reference_id: entry.reference_id,
                                edited_at: entry.edited_at,
                                edited_by: entry.edited_by,
                                _index: idx,
                              });
                              setEditLedgerOpen(true);
                            }}
                            data-testid={`edit-ledger-${entry.id || idx}`}
                          >
                            <Pencil className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Dialogs */}
      <ReceivePaymentDialog
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        customer={customer}
        balance={balance}
        onDone={load}
      />
      <RecordAdvanceDialog
        open={advanceOpen}
        onOpenChange={setAdvanceOpen}
        customer={customer}
        onDone={load}
      />
      <MarkCreditDialog
        open={creditOpen}
        onOpenChange={setCreditOpen}
        customer={customer}
        onDone={load}
      />
      <IssueRefundDialog
        open={refundOpen}
        onOpenChange={setRefundOpen}
        customer={customer}
        balance={balance}
        onDone={load}
      />
      <PayOutDialog
        open={payoutOpen}
        onOpenChange={setPayoutOpen}
        customer={customer}
        balance={balance}
        onDone={load}
      />
      <EditLedgerEntryDialog
        open={editLedgerOpen}
        onOpenChange={setEditLedgerOpen}
        customer={customer}
        entry={editLedgerEntry}
        onDone={load}
      />
      <SaleDetailDialog
        sale={viewSale}
        onOpenChange={(v) => { if (!v) setViewSale(null); }}
        onEdit={(s) => setEditSaleTarget(s)}
        onReturn={(s) => setReturnSaleTarget(s)}
        onCancel={(s) => setCancelSaleTarget(s)}
      />
      
      {/* Sale Edit Dialog */}
      <NewSaleDialog
        open={!!editSaleTarget}
        onOpenChange={(v) => { if (!v) setEditSaleTarget(null); }}
        shop={editSaleTarget ? { id: editSaleTarget.shop_id, name: editSaleTarget.shop_name } : null}
        onDone={load}
        editSale={editSaleTarget}
        editReason=""
      />
      
      {/* Sale Cancel Dialog */}
      <CancelSaleDialog
        sale={cancelSaleTarget}
        onOpenChange={(v) => { if (!v) setCancelSaleTarget(null); }}
        onDone={load}
      />
      
      {/* Sales Return Dialog */}
      <SalesReturnDialog
        open={!!returnSaleTarget}
        onOpenChange={(v) => { if (!v) setReturnSaleTarget(null); }}
        saleId={returnSaleTarget?.id}
        onSuccess={() => {
          setReturnSaleTarget(null);
          load();
        }}
      />
    </div>
  );
}
